import React, { useState } from 'react';
import { Sparkles, Loader2, RefreshCw, X, Images } from 'lucide-react';
import { ArtworkLibrary } from './ArtworkLibrary';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

// value: { art, artSubject } — onChange receives a patch
export function ArtworkPanel({ value, onChange, kind = 'illustration', accent = '#0D9488', piece = 'sign', aspect = 'square', defaultSubject = '', hint }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const subject = value?.artSubject || defaultSubject;

  const generate = async () => {
    if (!subject.trim()) { toast({ title: 'Describe the artwork first', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const r = await api.post('/generate-artwork', { kind, subject, accent, piece, aspect });
      onChange({ art: r.data.image, artSubject: subject });
      toast({ title: 'Artwork ready', description: 'Nano Banana illustration applied.' });
    } catch (e) { toast({ title: 'Artwork failed', description: e?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-fuchsia-400/30 bg-gradient-to-br from-fuchsia-500/10 to-violet-500/10 p-3 space-y-2" data-testid="artwork-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-fuchsia-300"><Sparkles className="w-3.5 h-3.5" /> AI Artwork</div>
        {value?.art && <button type="button" onClick={() => onChange({ art: '' })} className="text-[11px] text-red-500 flex items-center gap-0.5" data-testid="artwork-remove-btn"><X className="w-3 h-3" /> Remove</button>}
      </div>
      <div>
        <Label className="text-[11px] text-gray-500">What should it show?</Label>
        <Input value={subject} onChange={(e) => onChange({ artSubject: e.target.value })} className="h-8 text-xs" data-testid="artwork-subject-input" />
      </div>
      <div className="flex items-center gap-2">
        {value?.art && <img src={value.art} alt="artwork" className="h-10 w-14 object-contain rounded border bg-white" data-testid="artwork-thumb" />}
        <Button type="button" size="sm" onClick={generate} disabled={busy} className="flex-1 h-8 bg-gradient-to-r from-fuchsia-600 to-violet-600 hover:from-fuchsia-700 hover:to-violet-700 text-white" data-testid="artwork-generate-btn">
          {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Illustrating…</> : value?.art ? <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate</> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Generate artwork</>}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => setLibOpen(true)} className="h-8 border-fuchsia-300/40 text-fuchsia-200 bg-transparent hover:bg-fuchsia-500/10" title="Pick from saved artwork" data-testid="artwork-library-btn"><Images className="w-3.5 h-3.5 mr-1" /> Library</Button>
      </div>
      <ArtworkLibrary open={libOpen} onOpenChange={setLibOpen} kind={kind} onPick={(it) => { onChange({ art: it.image, artSubject: it.subject }); toast({ title: 'Artwork applied from library' }); }} />
      <p className="text-[10px] text-gray-400">{hint || 'Flat healthcare illustration, no text — saved to your Artwork Library automatically for reuse.'}</p>
    </div>
  );
}
